import type { MaterialPresetItem } from "../../../shared/api-contract";

/** The two material file formats: PBR (`.pmat`) and the diamond shader's (`.dmat`). */
export type MaterialFormat = "pmat" | "dmat";

/**
 * MIME types for a material file carried in a `data:` URL. Ours, not
 * registered ones; they exist so `usesDmat` can read the format back.
 */
const DATA_MIME: Record<MaterialFormat, string> = {
  pmat: "application/x-pmat+json",
  dmat: "application/x-dmat+json",
};

/**
 * Which RPC applies a curated material file.
 *
 * Routed on the FILE EXTENSION, not the category: the reference's "other"
 * bucket holds both .pmat and .dmat, so category is not a discriminator. The
 * category is only the tiebreak when a URL carries no recognisable extension.
 *
 * A material the workspace added itself has no CDN URL; it travels as a
 * `data:` URL (see `materialFileUrl`) whose MIME type names the format, and
 * that is read first.
 *
 * Getting this wrong is quiet rather than loud — `applyPmatUrlToGroup` on a
 * .dmat parses the JSON and produces a nonsense metal, it does not throw.
 */
export function usesDmat(fileUrl: string, category: MaterialPresetItem["category"]): boolean {
  const mime = /^data:([^,;]+)/i.exec(fileUrl)?.[1];
  if (mime === DATA_MIME.dmat) return true;
  if (mime === DATA_MIME.pmat) return false;
  if (/\.dmat(\?|#|$)/i.test(fileUrl)) return true;
  if (/\.pmat(\?|#|$)/i.test(fileUrl)) return false;
  return category === "gem";
}

/**
 * A material file's text as a URL the viewer can apply.
 *
 * The viewer only applies material files by URL (`applyPmatUrlToGroup`, which
 * also loads the file's textures and honours a declared DiamondMaterial). A
 * file the workspace uploaded is stored inline in its preset, and a `blob:`
 * URL cannot cross into the viewer's origin, so it is handed over as a
 * `data:` URL — `fetch()` reads those like any other.
 *
 * The `#/name.pmat` fragment is for the viewer's bookkeeping: it labels an
 * applied file with the URL's last path segment, which for a bare data: URL
 * is the whole encoded file — and that label is written into every saved
 * scene. `fetch()` drops a data: URL's fragment, so the payload is untouched.
 */
export function materialFileUrl(json: string, format: MaterialFormat, name = "material"): string {
  return `data:${DATA_MIME[format]};charset=utf-8,${encodeURIComponent(json)}#/${encodeURIComponent(name)}.${format}`;
}

/**
 * What a picked material file really is. The DECLARED type wins over the
 * extension: the reference ships `.pmat` files that declare
 * `"DiamondMaterial"`, and the viewer applies those as diamonds whatever they
 * are called. Knowing it up front lets the host give a metal part the diamond
 * shader before the file lands on it.
 */
export function materialFormatOf(fileName: string, parsed: unknown): MaterialFormat {
  const j = (parsed ?? {}) as { type?: unknown; metadata?: { type?: unknown }; isDiamondMaterialParameters?: unknown };
  const declared = typeof j.metadata?.type === "string" ? j.metadata.type : typeof j.type === "string" ? j.type : null;
  if (declared === "DiamondMaterial" || j.isDiamondMaterialParameters === true) return "dmat";
  if (declared && /Material$/.test(declared)) return "pmat";
  return /\.dmat$/i.test(fileName) ? "dmat" : "pmat";
}

/**
 * The swatch colour a material file declares, for drawing its sphere when it
 * has no rendered thumbnail. Both shapes occur: a decimal sRGB integer (webgi
 * and our own exports) and a "#rrggbb" string.
 */
export function materialFileColor(parsed: unknown): number | undefined {
  const c = (parsed as { color?: unknown } | null)?.color;
  if (typeof c === "number" && Number.isFinite(c)) return Math.max(0, Math.min(0xffffff, Math.round(c)));
  if (typeof c === "string" && /^#?[0-9a-f]{6}$/i.test(c.trim())) return parseInt(c.trim().replace("#", ""), 16);
  return undefined;
}
